/**
 * NotificationSettingsCard — Iter 396.
 *
 * Renders a toggle per muteable notification kind under Profile →
 * Settings. Fetches available kinds from
 * `GET /api/users/me/notification-kinds` (grouped by category) and
 * upserts the mute list via `PATCH /api/users/me/preferences`.
 */
import { useEffect, useState } from "react";
import { BellOff, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

export default function NotificationSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mutes, setMutes] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/users/me/notification-kinds`);
        if (cancelled) return;
        setCategories(data?.categories || []);
        setMutes(new Set(data?.current_mutes || []));
      } catch {
        if (!cancelled) { setCategories([]); setMutes(new Set()); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async (kind) => {
    const next = new Set(mutes);
    if (next.has(kind)) next.delete(kind); else next.add(kind);
    setMutes(next);
    setSaving(true);
    try {
      await api.patch(`/users/me/preferences`, {
        notification_mutes: [...next],
      });
    } catch (e) {
      toast.error("Could not save preference. Reverting.");
      // Revert on failure.
      if (next.has(kind)) next.delete(kind); else next.add(kind);
      setMutes(new Set(next));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="hv-card p-5" data-testid="notification-settings-card-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#647087]" />
      </div>
    );
  }

  if (categories.length === 0) return null;

  return (
    <div className="hv-card p-5" data-testid="notification-settings-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-cyan-500/15 border border-cyan-400/40 flex items-center justify-center shrink-0">
          <BellOff className="w-5 h-5 text-cyan-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl font-bold">Notification settings</h2>
          <p className="text-sm text-[#a0aabf] mt-1">
            Silence notification kinds you don&apos;t want in your bell. Muted kinds still fire for other users — this only affects you.
          </p>

          {categories.map((cat) => (
            <div key={cat.id} className="mt-5" data-testid={`notif-cat-${cat.id}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-300 font-bold mb-2">
                {cat.label}
              </div>
              <div className="space-y-1.5">
                {(cat.kinds || []).map((k) => {
                  const muted = mutes.has(k.kind);
                  return (
                    <label
                      key={k.kind}
                      className="flex items-start gap-3 p-2.5 rounded border border-white/5 hover:border-white/10 cursor-pointer bg-white/[0.015]"
                      data-testid={`notif-mute-row-${k.kind}`}
                    >
                      <input
                        type="checkbox"
                        checked={!muted}
                        onChange={() => toggle(k.kind)}
                        disabled={saving}
                        className="mt-0.5 w-4 h-4 accent-emerald-400"
                        data-testid={`notif-mute-toggle-${k.kind}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-medium ${muted ? "text-[#647087] line-through" : "text-white"}`}>
                          {k.label}
                        </div>
                        {k.body_hint && (
                          <div className="text-[11px] text-[#a0aabf] mt-0.5">{k.body_hint}</div>
                        )}
                      </div>
                      {muted && (
                        <span className="text-[9px] uppercase tracking-wider text-rose-300 font-mono shrink-0">
                          muted
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
